/**
 * CsvImportDialog — bulk-load Retail products from a CSV. Upserts by
 * barcode (GTIN). Provides a downloadable template + a row preview
 * before importing.
 */
import { useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";

const TEMPLATE_CSV =
  "name,price_aud,barcode,ingredients,best_before_days,allergens,storage\n" +
  "House Beef Pie,8.50,9312345678907,Beef;wheat flour;butter,3,gluten;milk;egg,refrigerated\n" +
  "ANZAC Biscuit 4pk,5.00,9312345678914,Oats;flour;sugar;butter,14,gluten,ambient\n";

export default function CsvImportDialog({ activeSite, onImported }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [importing, setImporting] = useState(false);

  const rows = csv.trim() ? csv.trim().split("\n").slice(1).filter(Boolean) : [];

  const download = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "retail_products_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (!csv.trim()) {
      toast.error("Paste or upload a CSV first");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/products/import-csv", { csv }, withSite(activeSite.id));
      const errCount = data.errors?.length || 0;
      toast.success(`Imported: ${data.created} created, ${data.updated} updated${errCount ? `, ${errCount} skipped` : ""}`);
      if (errCount) data.errors.slice(0, 3).forEach((msg) => toast.error(msg));
      setOpen(false);
      setCsv("");
      if (onImported) onImported();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button data-testid="csv-import-open" variant="outline" className="gap-2 border-eucalyptus-700 text-eucalyptus-900" onClick={() => setOpen(true)}>
        <Upload className="w-4 h-4" /> Import CSV
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Import retail products (CSV)</DialogTitle>
            <DialogDescription>
              Columns: name, price_aud, barcode, ingredients, best_before_days,
              allergens (semicolon-separated e.g. <code>gluten;milk</code>), storage.
              Rows are upserted by barcode.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button data-testid="csv-download-template" size="sm" variant="ghost" className="gap-2" onClick={download}>
                <Download className="w-4 h-4" /> Download template
              </Button>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer px-3 py-1.5 rounded border" style={{ borderColor: "var(--border)" }}>
                <FileSpreadsheet className="w-4 h-4" /> Upload .csv
                <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
              </label>
            </div>
            <textarea
              data-testid="csv-textarea"
              className="w-full min-h-[160px] font-mono text-xs border rounded p-2"
              style={{ borderColor: "var(--border)" }}
              placeholder={TEMPLATE_CSV}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
            />
            {rows.length > 0 && (
              <div className="text-sm text-charcoal-soft" data-testid="csv-row-count">{rows.length} data row(s) detected</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="csv-import-submit" disabled={importing} className="bg-ochre-600 hover:bg-ochre-400 text-white gap-2" onClick={doImport}>
              <Upload className="w-4 h-4" /> {importing ? "Importing\u2026" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
